import { fetchStrapi } from 'lib/api/strapi';
import {
  DefaultOfferAttributes,
  StrapiEntity,
  isSegmentEligibleForOffer,
  normalizeOfferPageData,
  parseMdcCodeFromSegment,
  simpleSegment,
} from 'lib/midas-offer';
import { MidasOfferPageData } from 'types/order-join-flow';
import { ReducedUserTokenState } from '../lib/utils/parseUser';

const OFFER_PAGE_PATH = '/api/midas-offer-pages';
const DEFAULT_OFFER_PATH = '/api/midas-default-offer-by-segment';
const DEFAULT_FETCH_OPTIONS = { noCache: true as const };
const FALLBACK_OFFER_SEGMENT = 'Anonymous';
const ESSENTIAL_PURCHASE_KEYS = ['atk', 'cio', 'cco', 'cookbook_collection'];
const CLASSES_PURCHASE_KEYS = ['school', 'classes'];
export const JOIN_STEP_2_VWO_ELIGIBLE_MDC = 'AF4116MA1D';
export const JOIN_STEP_2_VWO_CONTROL_MDC = 'AF4110MA1D';
export const JOIN_STEP_2_VWO_TREATMENT = 'Treatment-(JS-1.5)';
export const JOIN_STEP_2_VWO_EXP_ID =
  process.env.NEXT_PUBLIC_JOIN_STEP_2_VWO_EXP_ID || '981';
export const joinStep2VwoVariationLabels: Record<string, string> = {
  '1': 'Control',
  '2': JOIN_STEP_2_VWO_TREATMENT,
};

const BASE_SEGMENT_MAP: Record<string, string> = {
  Anonymous: 'Anonymous',
  Registrant: 'Registrant',
  Multisite: 'Member',
  Member: 'Member',
  Canceled: 'Canceled',
  TrialMember: 'TrialMember',
  RegistrantWithMag: 'Registrant',
  CanceledWithMag: 'Canceled',
  AnonymousWithMag: 'Anonymous',
};

export const getUserSegment = (user: ReducedUserTokenState) => {
  let segment = user.segment ?? 'anonymous';

  if (
    segment === 'cancelled_other' &&
    Array.isArray(user.cancelled_memberships) &&
    user.cancelled_memberships.length &&
    user.cancelled_memberships.every(
      (m) => !ESSENTIAL_PURCHASE_KEYS.includes(m),
    )
  ) {
    segment = 'registrant';
  }

  const active = Array.isArray(user.active_memberships)
    ? user.active_memberships
    : [];
  const cancelled = Array.isArray(user.cancelled_memberships)
    ? user.cancelled_memberships
    : [];
  const hasActiveClasses = active.some((m) =>
    CLASSES_PURCHASE_KEYS.includes(m),
  );
  const hasLapsedClasses =
    !hasActiveClasses &&
    cancelled.some((m) => CLASSES_PURCHASE_KEYS.includes(m));

  if (hasActiveClasses || hasLapsedClasses) {
    const normalized = simpleSegment(segment) || segment;
    let baseSegment = BASE_SEGMENT_MAP[normalized] ?? normalized;

    const hasActiveEssential = active.some((m) =>
      ESSENTIAL_PURCHASE_KEYS.includes(m),
    );
    const hasCancelledEssential = cancelled.some((m) =>
      ESSENTIAL_PURCHASE_KEYS.includes(m),
    );
    if (!hasActiveEssential && hasCancelledEssential) {
      baseSegment = 'Canceled';
    }

    const suffix = hasActiveClasses ? 'WithClasses' : 'WithLapsedClasses';
    return `${baseSegment}${suffix}`;
  }

  const normalized = simpleSegment(segment) || segment;
  return BASE_SEGMENT_MAP[normalized] ?? normalized;
};

const getOfferByMdc = async (mdcCode: string): Promise<MidasOfferPageData> => {
  const { data, error } = await fetchStrapi<Array<MidasOfferPageData>>(
    OFFER_PAGE_PATH,
    {
      filters: {
        mdc_code: {
          $eq: mdcCode,
        },
      },
      populate: 'deep',
    },
    DEFAULT_FETCH_OPTIONS,
  );

  if (error || !data) {
    throw new Error(`Failed to retrieve offer for MDC ${mdcCode}`);
  }

  const offer = data[0];

  if (!offer) {
    throw new Error(`No offer found for MDC code ${mdcCode}`);
  }

  return offer;
};

const getDefaultOfferConfig = async (
  segment: string,
): Promise<Array<StrapiEntity<DefaultOfferAttributes>>> => {
  const { data, error } = await fetchStrapi<
    | Array<StrapiEntity<DefaultOfferAttributes>>
    | StrapiEntity<DefaultOfferAttributes>
  >(
    DEFAULT_OFFER_PATH,
    {
      populate: 'deep',
    },
    DEFAULT_FETCH_OPTIONS,
  );

  if (error) {
    throw new Error(
      `Failed to retrieve default offer configuration for segment ${segment}`,
    );
  }

  if (!data) return [];

  return Array.isArray(data) ? data : [data];
};

const getDefaultOfferForSegment = async (
  segment: string,
): Promise<MidasOfferPageData> => {
  const response = await getDefaultOfferConfig(segment);
  const resolvedSegment = simpleSegment(segment) || segment;
  const mdcCode = parseMdcCodeFromSegment(response, resolvedSegment);
  return getOfferByMdc(mdcCode);
};

const determineOfferInternal = async (
  segment: string,
  mdcCode?: string,
): Promise<MidasOfferPageData | null> => {
  try {
    if (!segment) {
      throw new Error('No segment provided');
    }

    if (mdcCode) {
      try {
        const offer = await getOfferByMdc(mdcCode);
        if (isSegmentEligibleForOffer(segment, offer)) {
          return offer;
        }
      } catch {
        return await getDefaultOfferForSegment(segment);
      }
      // If the explicit offer is ineligible, continue with the segment's
      // default eligible offer instead of returning no offer.
    }
    return await getDefaultOfferForSegment(segment);
  } catch {
    return await getDefaultOfferForSegment(FALLBACK_OFFER_SEGMENT);
  }
};

export const determineSelfServiceOffer = async (options: {
  segment: string;
  mdcCode?: string;
}): Promise<MidasOfferPageData | null> => {
  const { segment, mdcCode } = options;
  const offer = await determineOfferInternal(segment, mdcCode);
  if (!offer) return null;
  return normalizeOfferPageData(offer);
};

export const shouldBucketJoinStep2Vwo = ({
  targetMdc,
  eligibleMdc,
}: {
  targetMdc?: string;
  eligibleMdc?: string;
}) => {
  if (targetMdc !== eligibleMdc || !eligibleMdc) return false;

  // Any selected MDC that remains eligible is included in the experiment
  // population. VWO then determines control vs. variation treatment.
  return targetMdc === JOIN_STEP_2_VWO_CONTROL_MDC;
};

export const getVwoCookie = (cookieName: string) => {
  if (typeof document === 'undefined') return undefined;

  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${cookieName}=`))
    ?.split('=')[1];

  return cookieValue ? decodeURIComponent(cookieValue) : undefined;
};

export const getJoinStep2VwoVariation = () => {
  const variationId = getVwoCookie(
    `_vis_opt_exp_${JOIN_STEP_2_VWO_EXP_ID}_combi`,
  );
  const variationLabel =
    (variationId && joinStep2VwoVariationLabels[variationId]) ||
    (variationId ? `Variation ${Number(variationId) - 1}` : 'not assigned');

  return { variationId, variationLabel };
};
